import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Download } from "lucide-react";
import { ESSAYS, QUOTES, SOCIALS } from "../data/mock";
import { useSEO } from "../hooks/useSEO";

/*
 * A plain-text / markdown view of the entire site, designed to be
 * cleanly parseable by LLMs, AI assistants and non-JS crawlers.
 * Follows the emerging `llms.txt` convention.
 */

const renderBlock = (b) => {
  switch (b.type) {
    case "h2":
      return `## ${b.text}`;
    case "h3":
      return `### ${b.text}`;
    case "blockquote":
      return `> ${b.text}`;
    case "list":
      return b.items.map((it) => `- ${it}`).join("\n");
    default:
      return b.text;
  }
};

const buildMarkdown = (origin) => {
  const lines = [];
  lines.push("# Hunar Syal");
  lines.push("");
  lines.push(
    "> Entrepreneur building brands and businesses in public. Writer. Host of Hangout with Hunar."
  );
  lines.push("");
  lines.push("## About");
  lines.push("");
  lines.push(
    "Hunar Syal is an entrepreneur who builds brands and businesses in public, and shares what he learns along the way."
  );
  lines.push("");
  lines.push(
    "He is currently building a travel company as a family business called **Hi Tours Group**, which operates three distinct brands in the travel industry:"
  );
  lines.push("");
  lines.push(
    "- **Hi Tours** (https://www.hitours.group), personalised leisure travel: luxury escapes and curated adventures."
  );
  lines.push(
    "- **Hi Offsite** (https://www.hitours.group/brands), corporate offsites, incentives and business travel for culture-first teams."
  );
  lines.push(
    "- **Hi DMC** (https://hidmc.in), destination management trusted by global tour operators across 15+ destinations."
  );
  lines.push("");
  lines.push(
    "Hunar has always been curious about entrepreneurship, business and technology, and specifically about how technology is changing the way people work. That curiosity is why he started **Hangout with Hunar**, a long-form conversation series with founders, creators, athletes, doctors, scientists and thinkers."
  );
  lines.push("");
  lines.push(
    "He also writes essays on life, mindset, entrepreneurship, relationships, growth and evolution, the themes he thinks matter most in your twenties and thirties."
  );
  lines.push("");
  lines.push(
    "Outside of work, Hunar likes to travel, play golf and go to the gym."
  );
  lines.push("");
  lines.push("### Interests");
  lines.push("- Travel");
  lines.push("- Entrepreneurship");
  lines.push("- Business");
  lines.push("- Tech and the future of work");
  lines.push("- Golf");
  lines.push("- The gym");
  lines.push("");
  lines.push("### Writing themes");
  lines.push(
    "Life, mindset, entrepreneurship, relationships, growth, evolution, discipline, money, career."
  );
  lines.push("");
  lines.push("## Links");
  lines.push(`- Instagram: ${SOCIALS.instagram}`);
  lines.push(`- YouTube: ${SOCIALS.youtube}`);
  lines.push(`- LinkedIn: ${SOCIALS.linkedin}`);
  lines.push(`- Contact (Instagram DM): ${SOCIALS.instagramDM}`);
  lines.push("");
  lines.push("## Essays");
  lines.push("");
  ESSAYS.forEach((e) => {
    lines.push(`### ${e.title}`);
    lines.push(
      `Category: ${e.category} · Tags: ${(e.tags || []).join(", ")} · Published: ${e.date} · Reading time: ${e.readTime}`
    );
    lines.push(`URL: ${origin}/essays/${e.slug}`);
    lines.push("");
    lines.push(`_${e.subtitle}_`);
    lines.push("");
    e.body.forEach((b) => {
      lines.push(renderBlock(b));
      lines.push("");
    });
    lines.push("---");
    lines.push("");
  });
  lines.push("## Quotes");
  lines.push("");
  QUOTES.forEach((q) => {
    lines.push(`### "${q.text}"`);
    lines.push(`URL: ${origin}/quotes/${q.slug}`);
    lines.push("");
    lines.push(q.meaning);
    lines.push("");
    lines.push("Hunar Syal");
    lines.push("");
    lines.push("---");
    lines.push("");
  });
  return lines.join("\n");
};

export const LLMView = () => {
  useSEO({
    title: "For AI & LLMs Hunar Syal",
    description:
      "Plain-text index of every essay and quote by Hunar Syal, formatted for AI assistants, LLMs and non-JS crawlers.",
    url: typeof window !== "undefined" ? window.location.href : undefined,
  });

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://hunarsyal.com";
  const markdown = buildMarkdown(origin);

  useEffect(() => {
    // Announce to bots that this page is the machine-readable version.
    const meta = document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "index, follow, max-snippet:-1");
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
    } catch {
      /* clipboard denied */
    }
  };

  const download = () => {
    const blob = new Blob([markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hunar-syal.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      data-testid="llm-view-page"
      className="min-h-screen"
      style={{ background: "var(--brand-cream)" }}
    >
      <div
        className="px-6 md:px-12 lg:px-20 py-6 flex items-center justify-between gap-4 sticky top-0 z-10 backdrop-blur-xl"
        style={{
          background: "color-mix(in srgb, var(--brand-cream) 90%, transparent)",
          borderBottom: "1px solid var(--brand-line)",
        }}
      >
        <Link
          to="/"
          data-testid="llm-back-link"
          className="inline-flex items-center gap-1.5 text-sm font-semibold hover:opacity-70 transition-opacity"
          style={{ color: "var(--brand-muted)" }}
        >
          <ArrowLeft size={15} strokeWidth={2} />
          Back to site
        </Link>
        <div className="flex items-center gap-3">
          <button
            onClick={copy}
            data-testid="llm-copy-button"
            className="inline-flex items-center gap-2 text-sm font-semibold rounded-full btn-ghost px-4 py-2"
          >
            <Copy size={14} strokeWidth={1.8} />
            Copy
          </button>
          <button
            onClick={download}
            data-testid="llm-download-button"
            className="inline-flex items-center gap-2 text-sm font-semibold rounded-full btn-primary px-4 py-2"
          >
            <Download size={14} strokeWidth={1.8} />
            Download .md
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3"
          style={{ color: "var(--brand-accent)" }}
        >
          Machine-readable
        </p>
        <h1
          className="text-3xl md:text-5xl font-bold tracking-tight mb-4"
          style={{ color: "var(--brand-ink)" }}
        >
          For AI &amp; LLMs
        </h1>
        <p
          className="text-base md:text-lg leading-relaxed mb-10"
          style={{ color: "var(--brand-muted)" }}
        >
          The entire site, every essay and every quote, as plain markdown,
          formatted for AI assistants, language models and crawlers that don't
          run JavaScript. Copy the whole thing or download it as{" "}
          <code>hunar-syal.md</code>.
        </p>

        <pre
          data-testid="llm-view-content"
          className="whitespace-pre-wrap break-words rounded-2xl p-6 md:p-8 text-[13px] leading-[1.7] font-mono overflow-x-auto"
          style={{
            background: "var(--brand-cream-soft)",
            border: "1px solid var(--brand-line)",
            color: "var(--brand-ink-soft)",
          }}
        >
          {markdown}
        </pre>
      </div>
    </div>
  );
};

export default LLMView;
